import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { EmployeeService } from 'app/main/global/components/employees/services/employee.service';
import { Employee } from '../../model/employee/employee.model';
import { MotoboyService } from 'app/main/global/components/motoboy/services/motoboy.service';
import { ReturnApi } from '../../model/return-api/returnApi-model';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Mask } from '@fagnerlima/ng-mask';
import { PostsService } from 'app/main/global/components/posts/services/posts.service';
import { Posto } from '../../model/posto/posto.model';

export interface DialogData {
  action: string;
  data: any;
}

@Component({
  selector: 'app-motoboy-form',
  templateUrl: './motoboy-form.component.html',
  styleUrls: ['./motoboy-form.component.scss']
})
export class MotoboyFormComponent implements OnInit {

  motoboy: FormGroup;
  titleHeader: string;
  maskPlaca = new Mask('AAA-AAAA');

  dialogMessages;
  disabledButton: boolean;
  
  private _onDestroy = new Subject<void>();

  @ViewChild('placa') placa: ElementRef;

  postos: Array<Posto>;

  public postoFilterControl: FormControl = new FormControl();
  public filteredPosto: ReplaySubject<Posto[]> = new ReplaySubject<Posto[]>(1);

  constructor(
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private postoService: PostsService,
    private motoboyService: MotoboyService,
    public matDialogRef: MatDialogRef<MotoboyFormComponent>,
    @Inject(MAT_DIALOG_DATA) public _data: DialogData) {

    this.postoService.listarPostos().subscribe(data => {
      this.postos = data.object;
      this.filteredPosto.next(this.postos.slice());
      this.postoFilterControl.valueChanges
        .pipe(takeUntil(this._onDestroy))
        .subscribe(() => {
          this.filterPosto();
        });
    });

    this.titleHeader = (this._data.action == 'new') ? 'Cadastro de Motoboy' : 'Edição de Motoboy';

  }

  setFormGroup(): void {

    let posto = null;
    if (this._data.action == 'update') {    
      posto = this._data.data.posto;  
    }

    this.motoboy = this.formBuilder.group({
      id: [this._data.data.id],
      nome: [this._data.data.nome, Validators.required],
      cpf: [this._data.data.cpf, [Validators.required, cpfValidator]],
      cidade: [this._data.data.cidade, Validators.required],
      estado: [this._data.data.estado, Validators.required],
      modeloMoto: [this._data.data.modeloMoto, Validators.required],
      placaMoto: [this._data.data.placaMoto, Validators.required],
      contatoZap: [this._data.data.contatoZap, Validators.required],
    });
  }

  ngOnInit(): void { 
    this.setFormGroup();
  }

  // tslint:disable-next-line: use-life-cycle-interface
  ngAfterViewInit(): void {
    setTimeout(() => this.placa.nativeElement.focus(), 1000);
  }

  onAdd(value): void {
    this.disabledButton = true;
    const placa = value.placaMoto.replace('-', '');
    value.placaMoto = placa.toUpperCase();
    this.motoboyService.create(value).subscribe(
      (ondata: ReturnApi) => {

        if (ondata.success) {
          this.dialogMessages = {
            type: 'success',
            title: 'Sucesso!',
            text: `${ondata.messages[0].text}`
          };
          this.feedback(this.dialogMessages);
          this.matDialogRef.close(ondata.object);
        } else {
          if (ondata.messages[0].text != null) {
            this.dialogMessages = {
              type: 'info',
              title: 'Informação!',
              text: `${ondata.messages[0].text}`
            };
          } else {
            this.dialogMessages = {
              type: 'error',
              title: 'Error!',
            };
          }

          this.feedback(this.dialogMessages);
          this.matDialogRef.close();
        }

      },
      (onerror) => {
        console.log('ERROR MOTOBOY', onerror);
      }
    );
  }

  onUpdate(value): void {
    this.disabledButton = true;
    const placa = value.placaMoto.replace('-', '');
    value.placaMoto = placa;

    this.motoboyService.update(value).subscribe(
      (ondata: ReturnApi) => {

        if (ondata.success) {
          this.dialogMessages = {
            type: 'success',
            title: 'Sucesso!',
            text: `${ondata.messages[0].text}`
          };
          this.feedback(this.dialogMessages);
          this.matDialogRef.close(ondata.object);

        } else {
          if (ondata.messages[0].text != null) {
            this.dialogMessages = {
              type: 'info',
              title: 'Informação!',
              text: `${ondata.messages[0].text}`
            };
          } else {
            this.dialogMessages = {
              type: 'error',
              title: 'Error!',
            };
          }

          this.feedback(this.dialogMessages);
          this.matDialogRef.close();
        }

      },
      (onerror) => {
        console.log('ERROR MOTOBOY', onerror);
      }
    );
  }

  private filterPosto(): void {
    if (!this.postos) {
      return;
    }

    let search = this.postoFilterControl.value;
    if (!search) {
      this.filteredPosto.next(this.postos.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredPosto.next(
      this.postos.filter(posto => posto.descricao.toLowerCase().indexOf(search) > -1)
    );
  }

  private feedback(message: string[]): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      data: message,
    });
    setTimeout(() => dialogRef.close(), 8000);
  }

  onClosed(): void {
    this.matDialogRef.close();
  }

}

// Validador de CPF customizado
function cpfValidator(control: FormControl): { [key: string]: boolean } | null {
  const cpf = control.value;
  if (!cpf) return null;

  const cleanCpf = cpf.replace(/\D/g, '');

  if (cleanCpf.length !== 11 || /^(\d)\1{10}$/.test(cleanCpf)) {
    return { invalidCpf: true };
  }

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
  }
  let rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cleanCpf.charAt(9))) {
    return { invalidCpf: true };
  }

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
  }
  rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(cleanCpf.charAt(10))) {
    return { invalidCpf: true };
  }

  return null;
}
